<template>
  <Teleport to="body">
    <div v-if="offlineQueue.syncModalOpen" class="oqm-overlay" @click.self="close">
      <div class="oqm-box" role="dialog" aria-modal="true">
        <div class="oqm-head">
          <span class="oqm-title">Offline Queue ({{ offlineQueue.totalUnsynced }})</span>
          <button type="button" class="oqm-close" @click="close">✕</button>
        </div>

        <div class="oqm-body">
          <p v-if="!offlineQueue.queue.length" class="oqm-empty">
            No queued requests on this device.
          </p>

          <!-- Shows ~5 rows; scrolls within for the rest. -->
          <div v-else class="oqm-list">
            <div v-for="item in offlineQueue.queue" :key="item.localId" class="oqm-item">
              <div class="oqm-item-top">
                <span class="oqm-type">
                  {{ typeLabel(item.type) }}
                  <span class="oqm-ol">{{ orderNo(item) }}</span>
                  <span v-if="item.serverInvoiceId" class="oqm-erp">→ {{ item.serverInvoiceId }}</span>
                </span>
                <span class="oqm-status" :class="'oqm-status--' + item.status">
                  <span v-if="item.status === 'processing'" class="oqm-spin" />
                  {{ item.status }}
                </span>
              </div>
              <div class="oqm-item-sub">
                <span>{{ formatTime(item.createdAt) }}</span>
                <span v-if="item.body && item.body.Total != null">Total: {{ item.body.Total }}</span>
                <span v-if="item.submitterEmail">{{ item.submitterEmail }}</span>
              </div>
              <div class="oqm-item-actions">
                <!-- The OL- receipt is saved locally at capture, so reprint works at any
                     stage — pending, processing, conflict, failed, or synced. -->
                <button type="button" class="oqm-reprint" @click="reprint(item)">🖨 Reprint receipt</button>
              </div>
            </div>
          </div>

          <p class="oqm-warn">
            ⚠ Stored only on this device until synced to the server. Clearing browser data,
            re-pairing, or using another device/browser will lose them.
          </p>
        </div>

        <div class="oqm-foot">
          <span class="oqm-foot-hint">
            <template v-if="offlineQueue.isSaving">
              <span class="oqm-spin oqm-spin--lg" /> Working…
            </template>
            <template v-else-if="!isOnline">Offline — will auto-sync when reconnected.</template>
            <template v-else-if="offlineQueue.syncCountdown != null">
              Auto-sync in <strong>{{ countdownLabel }}</strong>
            </template>
            <template v-else-if="offlineQueue.failedCount > 0" class="oqm-failed-hint">
              {{ offlineQueue.failedCount }} failed
            </template>
            <template v-else-if="offlineQueue.pendingCount > 0">{{ offlineQueue.pendingCount }} pending</template>
            <template v-else>All caught up.</template>
          </span>
          <div class="oqm-foot-actions">
            <button
              v-if="offlineQueue.syncedCount > 0"
              type="button"
              class="oqm-dismiss"
              @click="dismissSynced">
              Dismiss synced ({{ offlineQueue.syncedCount }})
            </button>
            <button
              v-if="offlineQueue.rejectedCount > 0"
              type="button"
              class="oqm-dismiss"
              @click="dismissRejected">
              Dismiss rejected ({{ offlineQueue.rejectedCount }})
            </button>
            <button
              v-if="offlineQueue.failedCount > 0"
              type="button"
              class="oqm-retry"
              :disabled="!isOnline || offlineQueue.isSaving"
              @click="retryFailed">
              Retry failed
            </button>
            <button
              type="button"
              class="oqm-sync"
              :disabled="!isOnline || offlineQueue.pendingCount === 0 || offlineQueue.isSaving"
              @click="syncNow">
              Sync now
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue';
import { useOfflineQueueStore } from '@/stores/offlineQueueStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from 'vue-toastification';
import { isOnline } from '@/services/healthCheck';

const offlineQueue = useOfflineQueueStore();
const orderStore = useOrderStore();
const authStore = useAuthStore();
const toast = useToast();

// Take a failed/conflicted offline order to its receipt for reprinting. Each offline
// order saved a receipt keyed by offlineLocalId; open it directly in the reprint view.
function reprint(item) {
  const receipt = orderStore.getSavedReceipts().find(r => r.offlineLocalId === item.localId);
  offlineQueue.syncModalOpen = false;
  if (receipt) {
    orderStore.setReceiptForReprint(receipt);
    authStore.toggleCheckoutPopup();
  } else {
    // No linked receipt — fall back to the saved-receipts menu so they can find it.
    orderStore.toggleDraftList();
    toast.info('No linked receipt found — opening saved receipts.');
  }
}

const countdownLabel = computed(() => {
  const s = offlineQueue.syncCountdown;
  if (s == null) return '';
  const m = Math.floor(s / 60);
  const sec = String(s % 60).padStart(2, '0');
  return `${m}:${sec}`;
});

// Manual override — skip the cooldown and sync right now (still per-item token auth).
function syncNow() { offlineQueue.syncNow(); }
function retryFailed() { offlineQueue.retryFailed(); }

// Clear all synced confirmations at once (receipts are kept in saved-receipts).
function dismissSynced() { offlineQueue.dismissSynced(); }
// Clear rejected (tamper/signature-rejected) items — terminal, they never sync.
function dismissRejected() { offlineQueue.dismissRejected(); }

// The offline order number printed on the receipt (OL-XXXXXXXX).
function orderNo(item) {
  return item.orderNumber || `OL-${(item.localId || '').slice(0, 8).toUpperCase()}`;
}

function close() { offlineQueue.syncModalOpen = false; }

function typeLabel(t) {
  return ({ order: 'Order', manual_order: 'Manual Order', error_log: 'Error Log' }[t]) ?? t;
}
function formatTime(ts) {
  try {
    return new Date(ts).toLocaleString('en-CA',
      { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}
</script>

<style scoped>
.oqm-overlay {
  position: fixed; inset: 0; z-index: 2147483000;
  background: rgba(15, 23, 42, 0.55);
  display: flex !important; align-items: center; justify-content: center;
  opacity: 1 !important; visibility: visible !important; padding: 1rem;
}
.oqm-box {
  background: #ffffff !important; color: #0f172a;
  width: min(560px, 95vw); max-height: 85vh; min-height: 160px;
  border-radius: 12px; box-shadow: 0 20px 50px rgba(0,0,0,0.35);
  display: flex; flex-direction: column; overflow: hidden;
}
.oqm-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.9rem 1.1rem; border-bottom: 1px solid #e2e8f0; flex: 0 0 auto;
}
.oqm-title { font-size: 1.05rem; font-weight: 700; }
.oqm-close { background: none; border: none; font-size: 1.15rem; cursor: pointer; color: #64748b; line-height: 1; }
.oqm-body { padding: 1rem 1.1rem; overflow-y: auto; flex: 1 1 auto; }
.oqm-empty { color: #94a3b8; font-size: 0.9rem; }
/* ~5 rows visible, then scroll within for more. */
.oqm-list { max-height: 22rem; overflow-y: auto; padding-right: 0.25rem; }
.oqm-item { border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.6rem 0.75rem; margin-bottom: 0.5rem; background: #f8fafc; }
.oqm-item-top { display: flex; justify-content: space-between; align-items: center; }
.oqm-type { font-weight: 700; color: #334155; }
.oqm-ol { font-weight: 600; font-size: 0.72rem; color: #5b21b6; background: #f5f3ff; border: 1px solid #ede9fe; border-radius: 5px; padding: 0.05rem 0.35rem; margin-left: 0.4rem; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.oqm-erp { font-weight: 600; font-size: 0.72rem; color: #166534; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 5px; padding: 0.05rem 0.35rem; margin-left: 0.3rem; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.oqm-status { font-size: 0.7rem; padding: 0.1rem 0.5rem; border-radius: 999px; background: #fde68a; color: #854d0e; text-transform: capitalize; }
.oqm-status--synced { background: #bbf7d0; color: #166534; }
.oqm-status--failed { background: #fecaca; color: #991b1b; }
.oqm-status--rejected { background: #fecaca; color: #991b1b; }
.oqm-item-sub { display: flex; gap: 0.9rem; flex-wrap: wrap; margin-top: 0.3rem; font-size: 0.78rem; color: #64748b; }
.oqm-item-actions { margin-top: 0.5rem; }
.oqm-reprint { background: #fff; color: #5b21b6; border: 1px solid #ddd6fe; border-radius: 6px; padding: 0.3rem 0.7rem; font-size: 0.78rem; font-weight: 600; cursor: pointer; }
.oqm-reprint:hover { background: #f5f3ff; }
.oqm-dismiss { background: #fff; color: #475569; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.3rem 0.7rem; font-size: 0.78rem; font-weight: 600; cursor: pointer; }
.oqm-dismiss:hover { background: #f1f5f9; }
.oqm-warn { margin-top: 0.75rem; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 0.6rem 0.8rem; font-size: 0.78rem; color: #7f1d1d; line-height: 1.5; }
.oqm-foot { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.8rem 1.1rem; border-top: 1px solid #e2e8f0; flex: 0 0 auto; }
.oqm-foot-hint { font-size: 0.78rem; color: #64748b; }
.oqm-foot-actions { display: flex; gap: 0.5rem; }
.oqm-sync { background: #7c3aed; color: #fff; border: none; border-radius: 8px; padding: 0.5rem 1.1rem; font-weight: 700; font-size: 0.85rem; cursor: pointer; }
.oqm-sync:disabled { background: #cbd5e1; color: #f8fafc; cursor: not-allowed; }
.oqm-retry { background: #fff; color: #b91c1c; border: 1px solid #fecaca; border-radius: 8px; padding: 0.5rem 1.1rem; font-weight: 700; font-size: 0.85rem; cursor: pointer; }
.oqm-retry:disabled { color: #cbd5e1; border-color: #e2e8f0; cursor: not-allowed; }
.oqm-foot-hint strong { color: #5b21b6; font-variant-numeric: tabular-nums; }
.oqm-spin { display: inline-block; width: 9px; height: 9px; border-radius: 50%;
  border: 2px solid #c4b5fd; border-top-color: #7c3aed; animation: oqm-spin 0.7s linear infinite;
  vertical-align: -1px; margin-right: 2px; }
.oqm-spin--lg { width: 11px; height: 11px; }
@keyframes oqm-spin { to { transform: rotate(360deg); } }
</style>
